/**
 * SecondaryPanel Component
 * Standardized wrapper for right-side secondary content panels
 *
 * Provides consistent sizing, styling, and DOM labeling across all integrations.
 * Column width mounts at target %; content enter uses shell-panel-motion.
 */

import { forwardRef } from 'react'
import { Panel, type ImperativePanelHandle } from 'react-resizable-panels'
import { useShellPanelEnterClass } from '@/hooks/use-shell-panel-motion'
import { PANEL_SIZES } from '@/lib/panel-sizes'
import type { SecondaryPanelProps } from './secondary-panel-types'
import { cn } from '@/lib/utils'

export const SecondaryPanel = forwardRef<ImperativePanelHandle, SecondaryPanelProps>(({
  children,
  defaultSize = PANEL_SIZES.SECONDARY_DEFAULT,
  minSize = PANEL_SIZES.SECONDARY_MIN,
  maxSize,
  className,
  background = 'muted',
  panelType,
  panelId,
  testId,
  collapsible = false,
  onClose,
  panelGroupId,
}, ref) => {
  const enterClass = useShellPanelEnterClass('right')

  const backgroundClasses = {
    muted: 'bg-muted/30',
    card: 'bg-card',
    transparent: 'bg-transparent',
  }

  return (
    <Panel
      ref={ref}
      defaultSize={defaultSize}
      minSize={minSize}
      maxSize={maxSize}
      className={cn(
        'overflow-hidden flex flex-col',
        backgroundClasses[background],
        className,
      )}
      data-testid={testId}
      data-secondary-panel
      data-panel-type={panelType}
      data-panel-id={panelId}
    >
      <div
        className={cn('flex h-full min-h-0 min-w-0 flex-1 flex-col', enterClass)}
        data-shell-panel-enter=""
      >
        {children}
      </div>
    </Panel>
  )
})

SecondaryPanel.displayName = 'SecondaryPanel'

